// Problem rule template as the dashboard shows it. Defaults MUST match functions-analytics/src/rules.js
// (the functions merge the saved template over the same defaults).

export const DEFAULT_RULES = {
  storageFree:   { enabled: true, warnPct: 15, critPct: 10, critGb: 1 },
  storageHealth: { enabled: true },
  crashes:       { enabled: true, warn: 1, crit: 3 },
  reboots:       { enabled: true, warn: 2, crit: 3 },
  cpuTemp:       { enabled: true, warn: 75, crit: 85 },
  ramFree:       { enabled: true, warnPct: 15, critPct: 10 },
  packetLoss:    { enabled: true, warn: 1, crit: 2 },
  latency:       { enabled: true, warn: 100, crit: 150 },
  healthScore:   { enabled: true, warn: 70, crit: 50 },
  flapping:      { enabled: true, warn: 3, crit: 5 },
  offlineHours:  { enabled: true, warnMin: 30, critMin: 60 },
  playerStopped: { enabled: true, minutes: 10 },
  silent:        { enabled: true, minutes: 10 },
  versionDrift:  { enabled: true },
  anomaly:       { enabled: true, factor: 3 },
  otaStuck:      { enabled: true, hours: 6 },
  inactive:      { enabled: true, days: 14 },
};

// 24/7 by default (owner 2026-10-29); a group gets its own hours only when added on the Rules page.
// Automatic "free up space" - MUST match functions-analytics/src/autoFreeSpace.js (owner 2026-10-29: 15%).
export const DEFAULT_AUTO_FREE_SPACE = { enabled: true, belowPct: 15, cooldownHours: 24, maxPerScan: 20, minFreedMb: 300 };
export const mergeAutoFreeSpace = (saved) => ({ ...DEFAULT_AUTO_FREE_SPACE, ...(saved || {}) });

// Automatic maintenance on online - MUST match functions-analytics/src/maintenance.js (owner 2026-10-30).
export const DEFAULT_MAINTENANCE = {
  enabled: true, installPlayer: true, installUpdater: true, excludePlayerGroups: ['AQUA%20LAB'], stabilizeSeconds: 30,
};
export const mergeMaintenance = (saved) => ({ ...DEFAULT_MAINTENANCE, ...(saved || {}) });

export const DEFAULT_HOURS = { from: '00:00', to: '24:00', days: [1, 2, 3, 4, 5, 6, 7] };

// fields: [key, label, unit] - "warn"/"crit" style keys get the amber / red treatment in the form.
export const RULE_META = {
  storageFree:   { section: 'device', label: 'Free storage', help: 'Free space on the device storage.', fields: [['warnPct', 'Warning below', '%'], ['critPct', 'Critical below', '%'], ['critGb', 'Critical below', 'GB']] },
  storageHealth: { section: 'device', label: 'Storage health', help: 'The storage state the monitoring app reports (warning / critical).', fields: [] },
  crashes:       { section: 'device', label: 'App crashes', help: 'Crashes in the last 24 hours.', fields: [['warn', 'Warning from', 'crashes'], ['crit', 'Critical from', 'crashes']] },
  reboots:       { section: 'device', label: 'Reboots', help: 'Boots in the last 24 hours.', fields: [['warn', 'Warning from', 'boots'], ['crit', 'Critical from', 'boots']] },
  cpuTemp:       { section: 'device', label: 'CPU temperature', help: 'Processor temperature.', fields: [['warn', 'Warning from', '°C'], ['crit', 'Critical from', '°C']] },
  ramFree:       { section: 'device', label: 'Free memory', help: 'Free RAM.', fields: [['warnPct', 'Warning below', '%'], ['critPct', 'Critical below', '%']] },
  packetLoss:    { section: 'device', label: 'Packet loss', help: 'Network packet loss.', fields: [['warn', 'Warning from', '%'], ['crit', 'Critical from', '%']] },
  latency:       { section: 'device', label: 'Network latency', help: 'Network round-trip time.', fields: [['warn', 'Warning from', 'ms'], ['crit', 'Critical from', 'ms']] },
  healthScore:   { section: 'device', label: 'Health score', help: 'The monitoring app\'s own 0-100 score.', fields: [['warn', 'Warning below', 'pts'], ['crit', 'Critical below', 'pts']] },
  flapping:      { section: 'fleet', label: 'Unstable connection', help: 'Times the device went offline today.', fields: [['warn', 'Warning from', 'times'], ['crit', 'Critical from', 'times']] },
  offlineHours:  { section: 'fleet', label: 'Offline too long', help: 'Offline time, counted inside the group\'s hours (24/7 unless a group has its own).', fields: [['warnMin', 'Warning from', 'min'], ['critMin', 'Critical from', 'min']] },
  playerStopped: { section: 'fleet', label: 'Player stopped', help: 'Device online but the player is not running.', fields: [['minutes', 'After', 'min']] },
  silent:        { section: 'fleet', label: 'Silent device', help: 'Reported online but not heard from (monitoring stopped).', fields: [['minutes', 'After', 'min']] },
  versionDrift:  { section: 'fleet', label: 'Outdated version', help: 'Player or monitoring older than the released version.', fields: [] },
  anomaly:       { section: 'fleet', label: 'Worse than usual', help: 'Today the device drops this many times more often than its own 7-day normal.', fields: [['factor', 'Factor', '×']] },
  otaStuck:      { section: 'fleet', label: 'OTA not installed', help: 'A newer OTA is set for the device\'s model but it has not installed it while online. Its logs are requested automatically.', fields: [['hours', 'After', 'hours']] },
  inactive:      { section: 'fleet', label: 'Inactive device', help: 'Offline longer than this is treated as stored / decommissioned, not as a problem.', fields: [['days', 'After', 'days']] },
};

export const PROBLEM_LABEL = Object.fromEntries(Object.entries(RULE_META).map(([k, v]) => [k, v.label]));

export const WEEKDAYS = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [7, 'Sun']];

/** Saved template merged over the defaults (same as the functions do). */
export function mergeRules(saved) {
  const out = {};
  Object.keys(DEFAULT_RULES).forEach((k) => { out[k] = { ...DEFAULT_RULES[k], ...((saved && saved[k]) || {}) }; });
  return out;
}

const minutesText = (m) => {
  if (m >= 1440) return `${Math.floor(m / 1440)} d ${Math.floor((m % 1440) / 60)} h`;
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
};

/** Short reading of a problem's measured value, e.g. "8% free", "84 °C", "2 h 10 min". */
export function problemValue(p) {
  const v = p.value;
  if (v === null || v === undefined || v === '') return '';
  switch (p.code) {
    case 'storageFree': case 'ramFree': return `${v}% free`;
    case 'crashes': return `${v} in 24 h`;
    case 'reboots': return `${v} boots in 24 h`;
    case 'cpuTemp': return `${v} °C`;
    case 'packetLoss': return `${v}%`;
    case 'latency': return `${v} ms`;
    case 'healthScore': return `${v}/100`;
    case 'flapping': case 'anomaly': return `${v}× today`;
    case 'offlineHours': case 'silent': return minutesText(Number(v));
    case 'playerStopped': return String(v).replace('_', ' ');
    default: return String(v);
  }
}
